import { useEffect, useMemo, useState } from 'react';
import { styled } from '@mui/material/styles';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import WarningAmberOutlinedIcon from '@mui/icons-material/WarningAmberOutlined';
import { Button } from '@bloomays-lib/ui.shared';
import { CRADashboardItemGQL, EditCraDaysManualInput } from '@bloomays-lib/types.shared';
import { fixed, totalWorkedDaysInCra } from '@bloomays-lib/utils.shared';
import { getDaysInMonth } from 'date-fns/getDaysInMonth';
import { useTranslation } from 'react-i18next';
import { errorLogger } from '../../helpers/error';
import { translateApolloError } from '@bloomays-lib/utils.shared';
import { notify } from '../../helpers/toastify';
import { businessDaysInMonth } from '../../helpers/craDashboard';

export type EditCraDaysFormProps = {
  row: CRADashboardItemGQL;
  onSave: (input: EditCraDaysManualInput) => Promise<void>;
  onCancel: () => void;
  saving?: boolean;
};

const DAY_FIELD_COLORS = {
  weekday: '#5B8DEF',
  saturday: '#E8B84A',
  sundayHoliday: '#E879A9',
  astreinte: '#6B5FCF',
} as const;

const formatInitialDayValue = (value: number): string => String(value);

/** Autorise une saisie décimale en cours d'édition (chaîne vide incluse). */
const sanitizeDayInput = (raw: string): string | null => {
  const normalized = raw.replace(',', '.');
  if (normalized === '') {
    return '';
  }
  if (/^\d*\.?\d*$/.test(normalized)) {
    return normalized;
  }
  return null;
};

const normalizeDayInputOnBlur = (raw: string): string => {
  const trimmed = raw.trim().replace(',', '.');
  if (trimmed === '' || trimmed === '.') {
    return '0';
  }
  const parsed = Number(trimmed);
  if (!Number.isFinite(parsed)) {
    return '0';
  }
  return String(parsed);
};

const parseDayInput = (raw: string): number => {
  const trimmed = raw.trim().replace(',', '.');
  if (trimmed === '' || trimmed === '.') {
    return 0;
  }
  const parsed = Number(trimmed);
  return Number.isFinite(parsed) ? parsed : 0;
};

const isNegativeDayValue = (value: number): boolean => value < 0;

type DayFieldRowProps = {
  color: string;
  label: string;
  value: string;
  invalid?: boolean;
  onChange: (value: string) => void;
  onBlur: () => void;
};

const DayFieldRow = ({ color, label, value, invalid = false, onChange, onBlur }: DayFieldRowProps) => (
  <FieldRow>
    <FieldLabelGroup>
      <ColorSwatch $color={color} aria-hidden />
      <FieldLabel>{label}</FieldLabel>
    </FieldLabelGroup>
    <NumericInput
      type="text"
      inputMode="decimal"
      value={value}
      $invalid={invalid}
      onChange={(event) => {
        const next = sanitizeDayInput(event.target.value);
        if (next !== null) {
          onChange(next);
        }
      }}
      onBlur={onBlur}
    />
  </FieldRow>
);

export const EditCraDaysForm = ({ row, onSave, onCancel, saving = false }: EditCraDaysFormProps) => {
  const { t } = useTranslation(['craDaysEdit', 'notify']);
  const activityMonth = row.activityMonth ? new Date(row.activityMonth) : null;

  const [normalWorkedDays, setNormalWorkedDays] = useState(formatInitialDayValue(row.nbDaysWorked ?? 0));
  const [saturdaysWorkedDays, setSaturdaysWorkedDays] = useState(formatInitialDayValue(row.nbDaysMarkupWorked ?? 0));
  const [sundayAndHolidaysWorkedDays, setSundayAndHolidaysWorkedDays] = useState(
    formatInitialDayValue(row.nbDaysMarkup2Worked ?? 0),
  );
  const [astreinteWorkedDays, setAstreinteWorkedDays] = useState(formatInitialDayValue(row.nbDaysAstreinteWorked ?? 0));
  const [reason, setReason] = useState('');
  const [saveError, setSaveError] = useState<string | null>(null);

  const parsedNormalWorkedDays = parseDayInput(normalWorkedDays);
  const parsedSaturdaysWorkedDays = parseDayInput(saturdaysWorkedDays);
  const parsedSundayAndHolidaysWorkedDays = parseDayInput(sundayAndHolidaysWorkedDays);
  const parsedAstreinteWorkedDays = parseDayInput(astreinteWorkedDays);

  const referenceTotal = fixed(
    totalWorkedDaysInCra({
      normalWorkedDays: row.nbDaysWorked ?? undefined,
      saturdaysWorkedDays: row.nbDaysMarkupWorked ?? undefined,
      sundayAndholidaysWorkedDays: row.nbDaysMarkup2Worked ?? undefined,
      astreinte: row.nbDaysAstreinteWorked ?? undefined,
    }),
  );
  const currentTotal = fixed(
    totalWorkedDaysInCra({
      normalWorkedDays: parsedNormalWorkedDays,
      saturdaysWorkedDays: parsedSaturdaysWorkedDays,
      sundayAndholidaysWorkedDays: parsedSundayAndHolidaysWorkedDays,
      astreinte: parsedAstreinteWorkedDays,
    }),
  );
  const expectedBusinessDays = activityMonth ? businessDaysInMonth(activityMonth) : 0;
  const daysInCalendarMonth = activityMonth ? getDaysInMonth(activityMonth) : 0;
  const totalDiffers = currentTotal !== referenceTotal;
  const reasonRequired = totalDiffers && reason.trim().length === 0;

  const invalidComponents = useMemo(
    () => ({
      normal:
        isNegativeDayValue(parsedNormalWorkedDays) ||
        (expectedBusinessDays > 0 && parsedNormalWorkedDays > expectedBusinessDays),
      saturday: isNegativeDayValue(parsedSaturdaysWorkedDays),
      sundayHoliday: isNegativeDayValue(parsedSundayAndHolidaysWorkedDays),
      astreinte: isNegativeDayValue(parsedAstreinteWorkedDays),
    }),
    [
      parsedNormalWorkedDays,
      parsedSaturdaysWorkedDays,
      parsedSundayAndHolidaysWorkedDays,
      parsedAstreinteWorkedDays,
      expectedBusinessDays,
    ],
  );
  const hasInvalidComponent = Object.values(invalidComponents).some(Boolean);
  const totalExceedsMonthDays = daysInCalendarMonth > 0 && currentTotal > daysInCalendarMonth;

  useEffect(() => {
    setSaveError(null);
  }, [normalWorkedDays, saturdaysWorkedDays, sundayAndHolidaysWorkedDays, astreinteWorkedDays, reason]);

  const handleSubmit = async () => {
    if (!row.activityId || hasInvalidComponent) {
      return;
    }
    setSaveError(null);
    try {
      await onSave({
        craAirtableId: row.activityId,
        normalWorkedDays: parsedNormalWorkedDays,
        saturdaysWorkedDays: parsedSaturdaysWorkedDays,
        sundayAndHolidaysWorkedDays: parsedSundayAndHolidaysWorkedDays,
        astreinteWorkedDays: parsedAstreinteWorkedDays,
        reason: reason.trim().length > 0 ? reason.trim() : undefined,
      });
    } catch (error) {
      errorLogger(error as Error, { extraInfos: 'CRA days manual edit save failed' });
      const message = translateApolloError(error, t);
      setSaveError(message);
      notify('error', message);
    }
  };

  return (
    <FormRoot>
      <EditCard>
        <EditCardHeader type="button" onClick={onCancel}>
          <EditOutlinedIcon fontSize="small" />
          {t('actionLabel', { ns: 'craDaysEdit' })}
        </EditCardHeader>

        <DecompositionTitle>{t('decompositionTitle', { ns: 'craDaysEdit' })}</DecompositionTitle>

        <FieldsStack>
          <DayFieldRow
            color={DAY_FIELD_COLORS.weekday}
            label={t('weekdayLabel', { ns: 'craDaysEdit' })}
            value={normalWorkedDays}
            invalid={invalidComponents.normal}
            onChange={setNormalWorkedDays}
            onBlur={() => setNormalWorkedDays((current) => normalizeDayInputOnBlur(current))}
          />
          <DayFieldRow
            color={DAY_FIELD_COLORS.saturday}
            label={t('saturdayLabel', { ns: 'craDaysEdit' })}
            value={saturdaysWorkedDays}
            invalid={invalidComponents.saturday}
            onChange={setSaturdaysWorkedDays}
            onBlur={() => setSaturdaysWorkedDays((current) => normalizeDayInputOnBlur(current))}
          />
          <DayFieldRow
            color={DAY_FIELD_COLORS.sundayHoliday}
            label={t('sundayHolidayLabel', { ns: 'craDaysEdit' })}
            value={sundayAndHolidaysWorkedDays}
            invalid={invalidComponents.sundayHoliday}
            onChange={setSundayAndHolidaysWorkedDays}
            onBlur={() => setSundayAndHolidaysWorkedDays((current) => normalizeDayInputOnBlur(current))}
          />
          <DayFieldRow
            color={DAY_FIELD_COLORS.astreinte}
            label={t('onCallLabel', { ns: 'craDaysEdit' })}
            value={astreinteWorkedDays}
            invalid={invalidComponents.astreinte}
            onChange={setAstreinteWorkedDays}
            onBlur={() => setAstreinteWorkedDays((current) => normalizeDayInputOnBlur(current))}
          />
        </FieldsStack>

        <TotalSummary>
          <TotalLabel>{t('totalDaysLabel', { ns: 'craDaysEdit' })}</TotalLabel>
          <TotalValue>{currentTotal}</TotalValue>
          <TotalSuffix>{t('totalDaysSuffix', { expected: expectedBusinessDays, ns: 'craDaysEdit' })}</TotalSuffix>
        </TotalSummary>

        {invalidComponents.normal ? (
          <FieldError>
            {parsedNormalWorkedDays < 0
              ? t('negativeValueHelper', { ns: 'craDaysEdit' })
              : t('normalDaysExceedsBusinessDays', { max: expectedBusinessDays, ns: 'craDaysEdit' })}
          </FieldError>
        ) : null}
        {invalidComponents.saturday || invalidComponents.sundayHoliday || invalidComponents.astreinte ? (
          <FieldError>{t('negativeValueHelper', { ns: 'craDaysEdit' })}</FieldError>
        ) : null}
      </EditCard>

      {totalExceedsMonthDays ? (
        <ImpactWarning role="alert">
          <WarningAmberOutlinedIcon fontSize="small" />
          <ImpactWarningText>
            {t('totalExceedsMonthDaysWarning', { max: daysInCalendarMonth, ns: 'craDaysEdit' })}
          </ImpactWarningText>
        </ImpactWarning>
      ) : null}

      <ReasonBlock>
        <ReasonLabel htmlFor="cra-days-edit-reason">{t('reasonLabelRecommended', { ns: 'craDaysEdit' })}</ReasonLabel>
        <ReasonTextarea
          id="cra-days-edit-reason"
          value={reason}
          placeholder={t('reasonPlaceholder', { ns: 'craDaysEdit' })}
          onChange={(event) => setReason(event.target.value)}
          $invalid={reasonRequired}
        />
        {reasonRequired ? <FieldError>{t('reasonRequired', { ns: 'craDaysEdit' })}</FieldError> : null}
      </ReasonBlock>

      {totalDiffers ? (
        <ImpactWarning role="alert">
          <WarningAmberOutlinedIcon fontSize="small" />
          <ImpactWarningText>
            <strong>{t('warningImpactTitle', { ns: 'craDaysEdit' })}</strong>{' '}
            {t('warningImpactBodyPrefix', { ns: 'craDaysEdit' })}{' '}
            <strong>{t('warningImpactBilling', { ns: 'craDaysEdit' })}</strong>{' '}
            {t('warningImpactBodyMiddle', { ns: 'craDaysEdit' })}{' '}
            <strong>{t('warningImpactPrime', { ns: 'craDaysEdit' })}</strong>
          </ImpactWarningText>
        </ImpactWarning>
      ) : null}

      {saveError ? <SaveError role="alert">{saveError}</SaveError> : null}

      <Actions>
        <Button textButton={t('cancelButton', { ns: 'craDaysEdit' })} onClick={onCancel} disable={saving} />
        <Button
          textButton={t('saveButton', { ns: 'craDaysEdit' })}
          onClick={() => void handleSubmit()}
          loading={saving}
          disable={saving || reasonRequired || hasInvalidComponent}
        />
      </Actions>
    </FormRoot>
  );
};

export default EditCraDaysForm;

const FormRoot = styled('div')(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(2),
}));

const EditCard = styled('div')(({ theme }) => ({
  backgroundColor: '#F4F7FB',
  borderRadius: 12,
  padding: theme.spacing(2),
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(1.5),
}));

const EditCardHeader = styled('button')(({ theme }) => ({
  display: 'inline-flex',
  alignItems: 'center',
  gap: 6,
  border: 'none',
  background: 'transparent',
  padding: 0,
  fontSize: 13,
  fontWeight: 600,
  fontFamily: '"Poppins Medium", sans-serif',
  color: theme.palette.blue[60],
  cursor: 'pointer',
  width: 'fit-content',
  '&:hover': { textDecoration: 'underline' },
}));

const DecompositionTitle = styled('div')(({ theme }) => ({
  fontSize: 13,
  fontWeight: 700,
  color: theme.palette.neutral[90],
}));

const FieldsStack = styled('div')(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(1.25),
}));

const FieldRow = styled('div')(() => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: 12,
}));

const FieldLabelGroup = styled('div')(() => ({
  display: 'inline-flex',
  alignItems: 'center',
  gap: 10,
  minWidth: 0,
}));

const ColorSwatch = styled('span')<{ $color: string }>(({ $color }) => ({
  width: 14,
  height: 14,
  borderRadius: 4,
  backgroundColor: $color,
  flexShrink: 0,
}));

const FieldLabel = styled('span')(({ theme }) => ({
  fontSize: 13,
  fontWeight: 500,
  color: theme.palette.neutral[70],
}));

const NumericInput = styled('input')<{ $invalid?: boolean }>(({ theme, $invalid }) => ({
  width: 64,
  height: 36,
  border: `1px solid ${$invalid ? theme.palette.red[60] : theme.palette.neutral[20]}`,
  borderRadius: 8,
  backgroundColor: theme.palette.neutral.white,
  textAlign: 'center',
  fontSize: 14,
  fontWeight: 600,
  color: theme.palette.neutral[90],
  fontFamily: '"Poppins Medium", sans-serif',
  '&:focus': {
    outline: `2px solid ${theme.palette.blue[40]}`,
    borderColor: theme.palette.blue[60],
  },
  '&::-webkit-outer-spin-button, &::-webkit-inner-spin-button': {
    WebkitAppearance: 'none',
    margin: 0,
  },
  MozAppearance: 'textfield',
}));

const TotalSummary = styled('div')(() => ({
  display: 'flex',
  alignItems: 'baseline',
  gap: 6,
  flexWrap: 'wrap',
}));

const TotalLabel = styled('span')(({ theme }) => ({
  fontSize: 13,
  fontWeight: 600,
  color: theme.palette.neutral[80],
}));

const TotalValue = styled('span')(({ theme }) => ({
  fontSize: 15,
  fontWeight: 700,
  color: theme.palette.neutral[90],
}));

const TotalSuffix = styled('span')(({ theme }) => ({
  fontSize: 13,
  fontWeight: 500,
  color: theme.palette.neutral[50],
}));

const ReasonBlock = styled('div')(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(0.75),
}));

const ReasonLabel = styled('label')(({ theme }) => ({
  fontSize: 13,
  fontWeight: 600,
  color: theme.palette.neutral[80],
}));

const ReasonTextarea = styled('textarea')<{ $invalid?: boolean }>(({ theme, $invalid }) => ({
  width: '100%',
  minHeight: 88,
  resize: 'vertical',
  borderRadius: 10,
  border: `1px solid ${$invalid ? theme.palette.red[60] : theme.palette.neutral[20]}`,
  backgroundColor: theme.palette.neutral.white,
  padding: '10px 12px',
  fontSize: 13,
  fontFamily: '"Poppins", sans-serif',
  color: theme.palette.neutral[90],
  '&::placeholder': {
    color: theme.palette.neutral[40],
  },
  '&:focus': {
    outline: `2px solid ${theme.palette.blue[40]}`,
    borderColor: theme.palette.blue[60],
  },
}));

const ImpactWarning = styled('div')(() => ({
  display: 'flex',
  alignItems: 'flex-start',
  gap: 10,
  padding: '12px 14px',
  borderRadius: 10,
  backgroundColor: '#FFF8E6',
  color: '#7A5B00',
}));

const ImpactWarningText = styled('p')(() => ({
  margin: 0,
  fontSize: 12.5,
  lineHeight: 1.45,
}));

const FieldError = styled('p')(({ theme }) => ({
  margin: 0,
  fontSize: 12,
  color: theme.palette.red[80],
}));

const SaveError = styled('p')(({ theme }) => ({
  margin: 0,
  fontSize: 13,
  fontWeight: 500,
  color: theme.palette.red[80],
}));

const Actions = styled('div')(({ theme }) => ({
  display: 'flex',
  justifyContent: 'flex-end',
  gap: theme.spacing(1),
}));
